<template>
  <div class="radio-group">
    <button
      v-for="option in normalizedOptions"
      :key="option.value"
      :class="[
        'radio-button',
        {
          'radio-button--active': modelValue === option.value,
          'radio-button--disabled': disabled
        }
      ]"
      :disabled="disabled"
      @click="handleClick(option.value)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export type RadioValue = string | number

interface RadioOption {
  value?: RadioValue
  label?: string
  [key: string]: any
}

interface NormalizedRadioOption {
  value: RadioValue
  label: string
  _original?: RadioOption
}

interface Props {
  modelValue?: RadioValue
  options: RadioOption[]
  disabled?: boolean
  labelKey?: string
  valueKey?: string
}

interface Emits {
  (e: 'update:modelValue', value: RadioValue): void
  (e: 'change', value: RadioValue, option?: RadioOption): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  disabled: false,
  labelKey: 'label',
  valueKey: 'value'
})

const emit = defineEmits<Emits>()

const normalizedOptions = computed((): NormalizedRadioOption[] => {
  return props.options.map(option => ({
    value: option[props.valueKey!] as RadioValue,
    label: option[props.labelKey!] as string,
    _original: option
  }))
})

const handleClick = (value: RadioValue) => {
  if (props.disabled) return

  const option = normalizedOptions.value.find(o => o.value === value)

  emit('update:modelValue', value)
  if (option) {
    emit('change', value, option._original || option)
  }
}
</script>

<style scoped>
.radio-group {
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.radio-button {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background-color: #ffffff;
  border: none;
  border-right: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
}

.radio-button:last-child {
  border-right: none;
}

.radio-button:hover:not(.radio-button--disabled):not(.radio-button--active) {
  background-color: #f9fafb;
  color: #374151;
}

.radio-button--active {
  background-color: #3b82f6;
  color: #ffffff;
}

.radio-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
